import { Toast, ToastAction, ToastClose, ToastDescription, ToastTitle } from '@recipes/ui';
import { useSyncExternalStore } from 'react';

export interface ToastItem {
  id: string;
  title?: string;
  description?: string;
  variant?: 'default' | 'destructive' | 'success';
  durationMs?: number;
  /** One action, e.g. Undo. The toast closes when it is used. */
  action?: { label: string; onAction: () => void };
}

type Listener = () => void;
let toasts: ToastItem[] = [];
const listeners = new Set<Listener>();
const emit = () => listeners.forEach((l) => l());

export const toast = {
  show(item: Omit<ToastItem, 'id'>): string {
    const id = crypto.randomUUID();
    toasts = [...toasts, { id, durationMs: 5000, ...item }];
    emit();
    return id;
  },
  success(title: string, description?: string) {
    return toast.show({ title, description, variant: 'success' });
  },
  error(title: string, description?: string) {
    return toast.show({ title, description, variant: 'destructive', durationMs: 8000 });
  },
  dismiss(id: string) {
    toasts = toasts.filter((t) => t.id !== id);
    emit();
  },
};

function subscribe(listener: Listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function Toaster() {
  const items = useSyncExternalStore(
    subscribe,
    () => toasts,
    () => toasts,
  );
  return (
    <>
      {items.map((item) => (
        <Toast
          key={item.id}
          variant={item.variant}
          duration={item.durationMs}
          onOpenChange={(open) => !open && toast.dismiss(item.id)}
        >
          <div className="grid gap-1">
            {item.title && <ToastTitle>{item.title}</ToastTitle>}
            {item.description && <ToastDescription>{item.description}</ToastDescription>}
          </div>
          {item.action && (
            <ToastAction
              altText={item.action.label}
              onClick={() => {
                item.action?.onAction();
                toast.dismiss(item.id);
              }}
              data-testid="toast-action"
            >
              {item.action.label}
            </ToastAction>
          )}
          <ToastClose />
        </Toast>
      ))}
    </>
  );
}
